import { notifications } from '@mantine/notifications';
import { IconExclamationCircle, IconQrcode } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCreateChargeMutation } from '../hooks/useCreateChargeMutation';
import MethodPanel from './MethodPanel';
import PromptPayQRModal from './PromptPayQRModal';
import PromptPaySuccessModal from './PromptPaySuccessModal';
import TopupAmountForm from './TopupAmountForm';

interface PromptPayState {
    qrCode: string;
    sourceID: string;
}

export default function PromptPayMethod() {
    const { t } = useTranslation('topup');
    const mutation = useCreateChargeMutation();
    const [amount, setAmount] = useState<number>(50);
    const [agreed, setAgreed] = useState(false);
    const [modal, setModal] = useState<PromptPayState | null>(null);
    const [completedAmount, setCompletedAmount] = useState<number | null>(null);

    const handleSubmit = async () => {
        if (!window.Omise) {
            notifications.show({
                title: t('toast.errorTitle'),
                message: t('errors.omiseUnavailable'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
            return;
        }

        await new Promise<void>((resolve) => {
            window.Omise.createSource(
                'promptpay',
                {
                    amount: amount * 100,
                    currency: 'THB',
                },
                async (statusCode, response) => {
                    if (statusCode !== 200) {
                        notifications.show({
                            title: t('toast.errorTitle'),
                            message: t('errors.sourceFailed'),
                            icon: <IconExclamationCircle size={18} color="#eb5757" />,
                        });
                        resolve();
                        return;
                    }
                    try {
                        const charge = await mutation.mutateAsync({
                            amount,
                            currency: 'THB',
                            redirectURL: window.location.origin,
                            sourceID: response.id,
                        });
                        setModal({ qrCode: charge.qrCode, sourceID: charge.sourceID });
                    } catch {
                        // Error already surfaced via mutation onError toast.
                    } finally {
                        resolve();
                    }
                }
            );
        });
    };

    return (
        <>
            <MethodPanel
                icon={IconQrcode}
                title={t('methods.promptpay.title')}
                tagline={t('methods.promptpay.tagline')}
            >
                <TopupAmountForm
                    amount={amount}
                    onAmountChange={setAmount}
                    agreed={agreed}
                    onAgreedChange={setAgreed}
                    submitting={mutation.isPending}
                    onSubmit={handleSubmit}
                />
            </MethodPanel>

            <PromptPayQRModal
                opened={modal !== null}
                qrCode={modal?.qrCode ?? ''}
                sourceID={modal?.sourceID ?? ''}
                onSuccess={(paidAmount) => {
                    setModal(null);
                    setCompletedAmount(paidAmount);
                }}
                onClose={() => setModal(null)}
            />

            <PromptPaySuccessModal
                opened={completedAmount !== null}
                amount={completedAmount ?? 0}
                onClose={() => setCompletedAmount(null)}
            />
        </>
    );
}
